import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { Chip, ChipProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  LMS_STATUS_TONE_CSS,
  LmsStatusTone,
  lmsStatusToneChipSx,
} from "@/components/lms/lmsStatusTone";

export type LmsStatusChipProps = Omit<
  ChipProps,
  "color" | "variant"
> & {
  tone: LmsStatusTone;
  variant?: ChipProps["variant"];
};

/** MUI Chip styled with shared LMS tone colors (not theme palette). */
export function LmsStatusChip({
  tone,
  sx,
  variant = "filled",
  ...rest
}: LmsStatusChipProps) {
  const toneSx = lmsStatusToneChipSx(tone);
  const mergedSx: SxProps<Theme> = sx
    ? ([toneSx, sx] as SxProps<Theme>)
    : toneSx;

  return <Chip variant={variant} sx={mergedSx} {...rest} />;
}

interface LmsStatusPillProps {
  tone: LmsStatusTone;
  label: string;
}

/** Inline pill matching the bottom status row on course cards (no Chip chrome). */
export function LmsStatusPill({ tone, label }: LmsStatusPillProps) {
  return (
    <span
      style={{
        alignItems: "center",
        borderRadius: "999px",
        display: "inline-flex",
        fontSize: "0.8125rem",
        fontWeight: 600,
        padding: "0.3rem 0.65rem",
        ...LMS_STATUS_TONE_CSS[tone],
      }}
    >
      {label}
    </span>
  );
}

interface LmsScormLessonStatusChipProps {
  lessonStatus: string;
  label: string;
  className?: string;
}

/** Header chip for SCORM lesson_status with icons mapped to LMS tones. */
export function LmsScormLessonStatusChip({
  lessonStatus,
  label,
  className,
}: LmsScormLessonStatusChipProps) {
  const normalized = lessonStatus.toLowerCase();
  const passed = ["completed", "passed"].includes(normalized);
  const failed = normalized === "failed";
  const tone: LmsStatusTone = passed
    ? "success"
    : failed
      ? "error"
      : "warning";
  const icon = passed ? (
    <CheckCircleOutlineIcon />
  ) : failed ? (
    <CloseIcon />
  ) : (
    <DriveFileRenameOutlineIcon />
  );

  return (
    <LmsStatusChip icon={icon} label={label} tone={tone} className={className} />
  );
}
